import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Autocomplete,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import AlertModal from "../../components/AlertModal";
import { hasWritePermission } from '../../utils/menuFilter';
import ConfirmModal from "../../components/ConfirmModal";

const Progress = () => {
  const canWrite = hasWritePermission('master.general-master');
  const [progressData, setProgressData] = useState([]);
  const [projectTypeData, setProjectTypeData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [projectTypeFilter, setProjectTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [errors, setErrors] = useState({});

  // Modal alert/confirm states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ title: '', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedProgressForDelete, setSelectedProgressForDelete] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [editingProgress, setEditingProgress] = useState(null);
  const [newProgress, setNewProgress] = useState({
    projectType: null,
    progressName: '',
    progressAlias: '',
    status: 'Active',
  });

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // Fetch project types for dropdown
  const fetchProjectTypes = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/globalMasterDetails?type=progress`,
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      const projectTypes = Array.isArray(result.data)
        ? result.data
          .filter((item) => item.status === true)
          .map((item) => ({
            id: item._id,
            name: item.details?.projectType || item.details?.name || 'Unknown',
          }))
        : [];

      setProjectTypeData(projectTypes);
    } catch (err) {
      console.error('Failed to load project types:', err);
    }
  };

  // Fetch progress
  const fetchProgress = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setApiError(null);
    try {
      const response = await fetch(
        `${API_BASE}/api/globalMasterDetails?type=progress`,
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      const progress = Array.isArray(result.data)
        ? result.data.map((item) => {
          const projectTypeId = item.details?.projectTypeId || item.details?.projectType;
          const projectType = projectTypeData.find((pt) => pt.id === projectTypeId);
          return {
            id: item._id,
            projectTypeId: projectTypeId,
            projectType: projectType?.name || 'Unknown',
            progressName: item.details?.progressName || item.details?.name || 'Unknown',
            progressAlias: item.details?.progressAlias || item.details?.alias || '',
            status: item.status === true ? 'Active' : 'Inactive',
            rawStatus: item.status,
          };
        })
        : [];

      setProgressData(progress);
    } catch (err) {
      setApiError('Failed to load progress.');
      setProgressData([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectTypes();
  }, []);

  useEffect(() => {
    if (projectTypeData.length > 0) {
      fetchProgress();
    }
  }, [projectTypeData]);

  // Filter logic
  const getFilteredProgress = () => {
    return progressData.filter((p) => {
      const matchesSearch =
        p.progressName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.progressAlias && p.progressAlias.toLowerCase().includes(searchQuery.toLowerCase()));
      // || p.projectType.toLowerCase().includes(searchQuery.toLowerCase());
      // const matchesProjectType =
      //   projectTypeFilter === 'all' || p.projectTypeId === projectTypeFilter;
      const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
      return matchesSearch && matchesStatus; // && matchesProjectType
    });
  };

  const filteredProgress = getFilteredProgress();

  // Pagination
  const paginatedProgress = filteredProgress.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // Validation
  const validateForm = () => {
    const err = {};
    // if (!newProgress.projectType) err.projectType = 'Project Type is required';
    if (!newProgress.progressName.trim()) err.progressName = 'Progress Name is required';
    return err;
  };

  // Duplicate check
  const isDuplicate = () => {
    return progressData.some(
      (p) =>
        p.progressName.toLowerCase() === newProgress.progressName.trim().toLowerCase() &&
        // p.projectTypeId === newProgress.projectType?.id &&
        p.id !== editingProgress?.id,
    );
  };

  // Handlers
  const handleOpenModal = () => {
    setEditingProgress(null);
    setNewProgress({
      projectType: null,
      progressName: '',
      progressAlias: '',
      status: 'Active',
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingProgress(null);
    setNewProgress({
      projectType: null,
      progressName: '',
      progressAlias: '',
      status: 'Active',
    });
    setErrors({});
  };

  const handleEdit = (progress) => {
    // const projectType = projectTypeData.find((pt) => pt.id === progress.projectTypeId);
    setEditingProgress(progress);
    setNewProgress({
      projectType: null, // projectType || null,
      progressName: progress.progressName,
      progressAlias: progress.progressAlias || '',
      status: progress.status,
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleDeleteClick = (progress) => {
    setSelectedProgressForDelete(progress);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedProgressForDelete) return;

    setIsLoading(true);
    setConfirmOpen(false);
    try {
      const response = await fetch(`${API_BASE}/api/globalMasterDetails/${selectedProgressForDelete.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isDeleted: true }),
      });

      if (!response.ok) throw new Error('Delete failed');

      setAlertConfig({ title: 'Success', message: 'Progress deleted successfully!' });
      setAlertOpen(true);
      fetchProgress(true);
    } catch (err) {
      setAlertConfig({ title: 'Error', message: 'Failed to delete progress.' });
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
      setSelectedProgressForDelete(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (isDuplicate()) {
      setErrors({ progressName: 'Progress name already exists for this project type' });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        type: 'progress',
        details: {
          // projectTypeId: newProgress.projectType.id,
          progressName: newProgress.progressName.trim(),
          progressAlias: newProgress.progressAlias.trim(),
        },
        status: newProgress.status === 'Active',
      };

      let response;
      if (editingProgress) {
        response = await fetch(`${API_BASE}/api/globalMasterDetails/${editingProgress.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch(`${API_BASE}/api/globalMasterDetails`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      if (!response.ok) throw new Error('Save failed');

      setAlertConfig({
        title: 'Success',
        message: editingProgress ? 'Progress updated successfully!' : 'Progress added successfully!'
      });
      setAlertOpen(true);
      handleCloseModal();
      fetchProgress(true);
    } catch (err) {
      setApiError('Failed to save progress.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchQuery('');
    setProjectTypeFilter('all');
    setStatusFilter('all');
    setPage(0);
    fetchProgress(true);
  };

  const handleChange = (field, value) => {
    setNewProgress((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HomeIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            Progress Master
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchProgress()}
            disabled={isLoading}
            sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Refresh
          </Button>
          {canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, flex: { xs: 1, sm: 'none' } }}
            >
              Add Progress
            </Button>
          )}
        </Box>
      </Box>

      {/* Modal alert/confirm states */}
      <AlertModal
        open={alertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertOpen(false)}
      />

      <ConfirmModal
        open={confirmOpen}
        title="Confirm Delete"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      >
        Are you sure you want to delete the progress <strong>{selectedProgressForDelete?.progressName}</strong>? This action cannot be undone.
      </ConfirmModal>

      {apiError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {apiError}
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => fetchProgress()}
            sx={{ ml: 2 }}
            size="small"
            variant="outlined"
          >
            Retry
          </Button>
        </Alert>
      )}

      {/* Search & Filter Section */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search Progress..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {/* <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Project Type</InputLabel>
                <Select
                  value={projectTypeFilter}
                  onChange={(e) => {
                    setProjectTypeFilter(e.target.value);
                    setPage(0);
                  }}
                  label="Project Type"
                >
                  <MenuItem value="all">All Project Types</MenuItem>
                  {projectTypeData.map((pt) => (
                    <MenuItem key={pt.id} value={pt.id}>
                      {pt.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid> */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    const count = filteredProgress.length;
                    setAlertConfig({
                      title: 'Search Results',
                      message: count > 0 ? `Found ${count} records matching your criteria.` : 'No records found matching your criteria.'
                    });
                    setAlertOpen(true);
                  }}
                  startIcon={<SearchIcon />}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleReset}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading && !progressData.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
                  {/* <TableCell sx={{ fontWeight: 600 }}>Project Type</TableCell> */}
                  <TableCell sx={{ fontWeight: 600 }}>Progress Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Alias</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedProgress.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {isLoading ? 'Loading...' : 'No progress found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedProgress.map((progress, index) => (
                    <TableRow key={progress.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      {/* <TableCell>{progress.projectType}</TableCell> */}
                      <TableCell>{progress.progressName}</TableCell>
                      <TableCell>{progress.progressAlias || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={progress.status}
                          color={progress.status === 'Active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {canWrite && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEdit(progress)}
                              disabled={isLoading}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                         {canWrite && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(progress)}
                              disabled={isLoading}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredProgress.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingProgress ? 'Edit Progress' : 'Add New Progress'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* <Grid item xs={12}>
                <Autocomplete
                  options={projectTypeData}
                  getOptionLabel={(option) => option.name}
                  value={newProgress.projectType}
                  onChange={(e, newValue) => handleChange('projectType', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Project Type"
                      error={!!errors.projectType}
                      helperText={errors.projectType}
                      required
                    />
                  )}
                />
              </Grid> */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Progress Name"
                  value={newProgress.progressName}
                  onChange={(e) => handleChange('progressName', e.target.value)}
                  error={!!errors.progressName}
                  helperText={errors.progressName}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Progress Alias"
                  value={newProgress.progressAlias}
                  onChange={(e) => handleChange('progressAlias', e.target.value)}
                  helperText="Enter alias code or identifier"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newProgress.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseModal} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              {editingProgress ? 'Update' : 'Add'} Progress
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Progress;

